import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockKeyhole, MessageCircleMore, Paperclip } from "lucide-react";
import { useState } from "react";

import { PageError, PageLoading } from "@/components/PageState";
import { EmptyState } from "@/components/ui/empty-state";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { ConversaUnificada, RespostaPronta } from "@/lib/types";
import { cn } from "@/lib/utils";

import { CabecalhoDaConversa } from "./CabecalhoDaConversa";
import type { JanelaDaConversa } from "./ConversaInterna";
import { CompositorDaCaixa, type RascunhosDaCaixa } from "./CompositorDaCaixa";

/**
 * TUT-491 — a conversa do atendimento (WhatsApp, e-mail…) no meio da caixa
 * única: o histórico da Tutelae Inbox e o compositor com "Responder contato" ×
 * "Nota interna" (a nota, só a equipe vê) e o `/` das respostas prontas no
 * responder. Era o miolo da antiga aba Atendimento (`CaixaDeAtendimento`).
 */
export function ConversaExterna({
  conversa,
  rascunhos,
  respostas,
  processo,
  onMudou,
  onVoltar,
  onContato,
  janela,
}: {
  conversa: ConversaUnificada;
  rascunhos: RascunhosDaCaixa;
  respostas: readonly RespostaPronta[] | null;
  processo: string | null;
  /** Avisa a caixa (recarrega só a primeira página, M1). */
  onMudou: () => void;
  onVoltar?: () => void;
  onContato?: () => void;
  /** TUT-590 — a conversa numa janela do chat rápido. */
  janela?: JanelaDaConversa;
}) {
  const queryClient = useQueryClient();
  const numero = Number(conversa.id.slice(3));
  const [feedback, setFeedback] = useState("");
  const mensagens = useQuery({
    queryKey: ["tutelae-inbox", "messages", numero],
    queryFn: () => api.inboxMessages(numero),
  });
  const enviar = useMutation({
    mutationFn: (valor: { texto: string; nota: boolean }) => api.sendInboxMessage(numero, valor.texto, valor.nota),
    onSuccess: async (_dado, valor) => {
      setFeedback(valor.nota ? "Nota interna salva." : "Mensagem enviada.");
      onMudou();
      await queryClient.invalidateQueries({ queryKey: ["tutelae-inbox", "messages", numero] });
    },
  });
  const primeiroNome = conversa.titulo.split("·")[0]?.trim() ?? conversa.titulo;

  return (
    <>
      <CabecalhoDaConversa
        conversa={conversa}
        subtitulo={conversa.contato?.telefone ?? conversa.contato?.email ?? ""}
        onVoltar={onVoltar}
        onContato={onContato}
        acoes={janela?.acoes}
        idDoTitulo={janela?.idDoTitulo}
      />
      <div
        className={cn(
          "flex flex-1 flex-col gap-3 overflow-y-auto bg-surface-2",
          janela ? "min-h-0 p-3" : "min-h-[18rem] p-4",
        )}
        data-atendimento-thread
        role="log"
        aria-label="Mensagens"
      >
        {mensagens.isLoading ? (
          <PageLoading label="Carregando mensagens..." />
        ) : mensagens.isError ? (
          <PageError message="Não foi possível ler esta conversa." onRetry={() => void mensagens.refetch()} />
        ) : mensagens.data?.items.length ? (
          mensagens.data.items.map((item) => (
            <article
              key={item.id}
              className={cn(
                "max-w-[88%] rounded-xl border border-border px-3 py-2 shadow-sm",
                item.private
                  ? "ml-auto border-dashed bg-[var(--warning-highlight)]"
                  : item.direction === "outgoing"
                    ? "ml-auto bg-[var(--primary-highlight)]"
                    : "mr-auto bg-surface",
              )}
            >
              {item.private ? (
                <span className="mb-1 inline-flex items-center gap-1 text-legenda font-semibold text-[var(--warning)]">
                  <LockKeyhole aria-hidden="true" className="size-3.5" />
                  Nota interna · só a equipe vê
                </span>
              ) : null}
              <p className="m-0 whitespace-pre-wrap break-words text-corpo">{item.content || "Mensagem sem texto"}</p>
              {item.attachments.length ? (
                <span className="mt-2 inline-flex items-center gap-1 text-legenda text-[var(--muted)]">
                  <Paperclip aria-hidden="true" className="size-3.5" />
                  {item.attachments.length} {item.attachments.length === 1 ? "anexo" : "anexos"}
                </span>
              ) : null}
              <footer className="mt-1 flex justify-end gap-2 text-legenda text-[var(--muted)]">
                {item.sender?.name ? <span>{item.sender.name}</span> : null}
                {item.createdAt ? <time>{formatDateTime(item.createdAt)}</time> : null}
              </footer>
            </article>
          ))
        ) : (
          <EmptyState>
            <div>
              <MessageCircleMore className="mx-auto size-8" aria-hidden="true" />
              <p>A conversa ainda não tem mensagens.</p>
            </div>
          </EmptyState>
        )}
      </div>
      {/* M4 — a falha do envio é avisada UMA vez, pelo compositor (que guarda o
          texto); aqui fica só a confirmação. */}
      <p className="m-0 px-4 pt-2 text-legenda text-[var(--success)]" aria-live="polite" data-atendimento-feedback>
        {feedback}
      </p>
      <CompositorDaCaixa
        key={conversa.id}
        conversaId={conversa.id}
        rascunhos={rascunhos}
        idDoCampo={janela?.idDoCampo ?? "atendimento-message"}
        compacto={Boolean(janela)}
        comModos
        limite={10_000}
        enviando={enviar.isPending}
        respostas={janela ? null : respostas}
        contexto={{ nome: conversa.titulo, processo }}
        rotulo={(modo) => (modo === "nota" ? "Nota interna para a equipe" : "Responder ao contato")}
        placeholder={(modo) =>
          modo === "nota"
            ? "Registre um contexto que só a equipe verá..."
            : janela
              ? "Escreva uma resposta..."
              : "Escreva uma resposta... (/ para respostas prontas)"
        }
        textoDoBotao={(modo) => (modo === "nota" ? "Salvar nota" : "Enviar")}
        aviso={(modo) =>
          modo === "nota"
            ? "Só a equipe vê esta nota."
            : `Vai para ${primeiroNome} pelo ${conversa.canalRotulo}.`
        }
        onEnviar={(texto, modo) => {
          setFeedback("");
          return enviar.mutateAsync({ texto, nota: modo === "nota" });
        }}
      />
    </>
  );
}
